import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaceDetection } from '@mediapipe/face_detection';
import { Camera } from '@mediapipe/camera_utils';
import ScreenshotManager from '../../utils/ScreenshotManager';
import { useObjectDetection } from '../../hooks/useObjectDetection';
import { useVoiceAnalysis } from '../../hooks/useVoiceAnalysis';
import { useExternalMonitor } from '../../hooks/useExternalMonitor';
import { useLipSyncDetection } from '../../hooks/useLipSyncDetection';
import './ProctoringMonitor.css';
import { getPublicApiOrigin } from '../../config/env';
import { isStreamLive } from '../../utils/interviewPrep';
import { LocalFaceIdentity, IDENTITY_MISMATCH_MSG } from '../../utils/faceIdentityCheck';

// Minimum face-detection confidence for the report header photo (profile_photo).
const PROFILE_PHOTO_MIN_SCORE = 0.8;

// Eye gaze — relaxed thresholds + sustained look-away before reporting
const EYE_GAZE_AWAY_THRESHOLD_MS = 5000;
const EYE_GAZE_VIOLATION_THROTTLE_MS = 10000;
const GAZE_X_THRESHOLD = 0.30;
const GAZE_Y_THRESHOLD = 0.82;
const GAZE_MIN_FACE_WIDTH = 0.08;
const GAZE_MIN_SCORE = 0.45;

const PROCTORING_SEVERITY_MAP = {
  eye_gaze_away: 'high',
  tab_switch: 'high',
  window_blur: 'medium',
  multiple_faces: 'high',
  multiple_voices: 'high',
  audio_anomaly: 'medium',
  face_not_visible: 'high',
  external_monitor: 'medium',
  lip_sync_mismatch: 'high',
  object_detected: 'medium',
  multiple_persons: 'high',
  candidate_identity_mismatch: 'critical',
};

export const ProctoringMonitor = ({
  interviewId,
  sessionId,
  recordingStartTime,
  onViolation,
  showCameraOnly = false,
  onScreenshotReady,
  onVideoReady,
  videoRecorderRef,
  onReady,
  prewarmedStream = null,
  prewarmedFaceDetection = null,
  prewarmedReady = false,
  /** When false, tab/blur are owned by useBrowserSecurity in the parent room. */
  manageBrowserEvents = true,
  /** Pixel-based "same person" check. Off: it raised false identity flags (lighting, movement). */
  identityCheckEnabled = false,
  /** Keep camera tracks alive on unmount (AI → coding continuous recording). */
  preserveStreamOnUnmount = false,
}) => {
  // Use sessionId if provided, otherwise fallback to interviewId
  const activeId = sessionId || interviewId;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [eyeContact, setEyeContact] = useState('Good');
  const [tabSwitches, setTabSwitches] = useState(0);
  const [stream, setStream] = useState(null);
  const lastEyeContactTime = useRef(Date.now());
  const awayStartTimeRef = useRef(null);
  const violationCountRef = useRef(0);
  const lastViolationTime = useRef({}); // Track last violation time per type
  const lastFaceCountRef = useRef(0);
  const fatalErrorRef = useRef(false);
  const VIOLATION_THROTTLE_MS = 2000; // Only report same violation type once per 2 seconds
  const NO_FACE_THRESHOLD = 15; // ~1.5s depending on camera frame cadence
  const identityCheckerRef = useRef(identityCheckEnabled ? new LocalFaceIdentity() : null);
  const identityFrameSkipRef = useRef(0);
  const lastIdentityReportRef = useRef(0);
  const preserveStreamOnUnmountRef = useRef(preserveStreamOnUnmount);
  preserveStreamOnUnmountRef.current = preserveStreamOnUnmount;

  // Screenshot management
  const screenshotManagerRef = useRef(null);
  const firstFaceDetectedRef = useRef(false);
  // Report header photo: one clear, single-face frame during the face-only intro.
  const profilePhotoTakenRef = useRef(false);
  const randomScreenshotIntervalRef = useRef(null);
  const lastViolationScreenshotRef = useRef({});
  const sessionScreenshotCountRef = useRef(0);
  const MAX_SESSION_SCREENSHOTS = 15;
  // Step 10 — guardrails. Separate budget/interval from the violation-
  // evidence screenshots above: those are rare and capped by design, this
  // needs continuous coverage through the whole interview for content
  // moderation, sampled frames not the whole stream.
  const moderationSampleIntervalRef = useRef(null);
  const moderationSampleCountRef = useRef(0);
  const MODERATION_SAMPLE_INTERVAL_MS = 15000;
  const MAX_MODERATION_SAMPLES = 200;
  const recordingStartTimeRef = useRef(recordingStartTime);
  useEffect(() => {
    recordingStartTimeRef.current = recordingStartTime;
  }, [recordingStartTime]);

  const analyzeGaze = (detection) => {
    if (!detection || !detection.boundingBox) return { away: true, direction: 'unknown' };
    const box = detection.boundingBox;
    const xCenter = box.xCenter ?? 0.5;
    const yCenter = box.yCenter ?? 0.5;
    const faceWidth = box.width ?? 0.2;
    const score = Array.isArray(detection.score)
      ? (detection.score[0] ?? 1)
      : (detection.score ?? 1);

    // Determine direction before deciding away (thresholds aligned with away check)
    let direction = 'center';
    if (Math.abs(xCenter - 0.5) > GAZE_X_THRESHOLD) {
      direction = xCenter < 0.5 ? 'right' : 'left';
      // Note: camera mirrors horizontally — xCenter < 0.5 = looking right
    }
    if (yCenter > GAZE_Y_THRESHOLD) direction = 'down';
    if (faceWidth < GAZE_MIN_FACE_WIDTH) direction = 'turned';
    if (score < GAZE_MIN_SCORE) direction = 'obscured';

    const away =
      Math.abs(xCenter - 0.5) > GAZE_X_THRESHOLD ||
      yCenter > GAZE_Y_THRESHOLD ||
      faceWidth < GAZE_MIN_FACE_WIDTH ||
      score < GAZE_MIN_SCORE;

    return { away, direction };
  };

  const reportViolation = useCallback(
    async (type, message, details = {}, severityOverride) => {
      // Browser FFT cannot tell solo speech + interviewer TTS from a second person — too many false positives.
      if (type === 'multiple_voices') {
        return;
      }

      const now = Date.now();
      const lastReportTime = lastViolationTime.current[type] || 0;
      const throttleMs =
        type === 'eye_gaze_away' ? EYE_GAZE_VIOLATION_THROTTLE_MS : VIOLATION_THROTTLE_MS;

      if (now - lastReportTime < throttleMs) {
        return;
      }

      violationCountRef.current += 1;
      lastViolationTime.current[type] = now;

      if (onViolation) {
        onViolation({ type, message, timestamp: new Date() });
      }

      if (!activeId) {
        console.warn('⚠️ Cannot report violation: No session ID or interview ID');
        return;
      }

      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) {
          console.warn('⚠️ Cannot report violation: No access token');
          return;
        }

        await fetch(`${getPublicApiOrigin()}/api/proctoring/${activeId}/event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            event_type: type,
            severity: severityOverride ?? PROCTORING_SEVERITY_MAP[type] ?? 'medium',
            metadata: {
              message,
              videoOffsetSeconds: recordingStartTimeRef.current
                ? parseFloat(((Date.now() - recordingStartTimeRef.current) / 1000).toFixed(3))
                : null,
              ...details,
            },
          }),
        });
      } catch (error) {
        console.error('Error reporting violation:', error);
      }
    },
    [onViolation, activeId]
  );

  const handleObjectViolation = useCallback(
    (payload) => {
      reportViolation(
        payload.type,
        payload.message,
        payload.details || {},
        payload.severity
      );
    },
    [reportViolation]
  );

  const { detectFrame, cleanup: cleanupObjectDetection } = useObjectDetection({
    onViolation: handleObjectViolation,
    enabled: true,
  });

  const { stop: stopVoice } = useVoiceAnalysis({
    onViolation: handleObjectViolation,
    enabled: false,
  });

  const { start: startMonitorCheck, stop: stopMonitorCheck } = useExternalMonitor({
    onViolation: handleObjectViolation,
    enabled: true,
  });

  const {
    analyzeFrame: analyzeLipSync,
    start: startLipSync,
    stop: stopLipSync,
  } = useLipSyncDetection({
    onViolation: handleObjectViolation,
    enabled: true,
  });

  const detectFrameRef = useRef(detectFrame);
  detectFrameRef.current = detectFrame;

  const analyzeLipSyncRef = useRef(analyzeLipSync);
  analyzeLipSyncRef.current = analyzeLipSync;
  const faceDetectionRef = useRef(null);
  const onReadyCalledRef = useRef(false);
  const prewarmedStreamRef = useRef(prewarmedStream);
  const usingPrewarmedStreamRef = useRef(isStreamLive(prewarmedStream));

  useEffect(() => {
    if (!videoRef.current || !stream || !stream.active) return;
    const tracks = stream.getVideoTracks();
    if (tracks.length === 0) return;
    onVideoReady?.(videoRef.current);
  }, [stream, onVideoReady]);

  useEffect(() => {
    const pwStream = prewarmedStreamRef.current;
    const livePrewarmedStream = isStreamLive(pwStream);
    usingPrewarmedStreamRef.current = livePrewarmedStream;

    let faceDetection = null;
    let camera = null;
    let animationFrameId = null;
    let isProcessing = false;
    let noFaceFrameCount = 0;
    let multipleFaceFrameCount = 0;
    let fatalFallbackUsed = false;
    let cancelled = false;

    if (faceDetectionRef.current) return;

    if (typeof FaceDetection === 'undefined') {
      console.warn('⚠️ MediaPipe FaceDetection not available, skipping proctoring');
      return;
    }

    const waitForVideoReady = (videoEl) =>
      new Promise((resolve) => {
        if (!videoEl) {
          resolve();
          return;
        }
        if (videoEl.readyState >= 2 && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
          resolve();
          return;
        }
        videoEl.addEventListener('loadedmetadata', () => resolve(), { once: true });
      });

    const attachOnResults = (fd) => {
      fd.onResults((results) => {
      try {
        lastFaceCountRef.current = results.detections?.length ?? 0;

        if (results.detections && results.detections.length > 0) {
          const detection = results.detections[0];
          setFaceDetected(true);
          
          // ✅ FEATURE 2: Capture screenshot on first face detection
          if (!firstFaceDetectedRef.current && screenshotManagerRef.current) {
            firstFaceDetectedRef.current = true;
            screenshotManagerRef.current.capture('face_detection', {
              confidence: detection.score,
              faceCount: results.detections.length
            }).then(screenshot => {
              if (screenshot && onScreenshotReady) {
                onScreenshotReady(screenshot);
              }
            });
          }
          
          // Report photo (D-044): first frame with exactly one confident face.
          if (
            !profilePhotoTakenRef.current
            && screenshotManagerRef.current
            && results.detections.length === 1
            && (detection.score?.[0] ?? detection.score ?? 0) >= PROFILE_PHOTO_MIN_SCORE
          ) {
            profilePhotoTakenRef.current = true;
            screenshotManagerRef.current.capture('profile_photo', {
              confidence: detection.score,
              faceCount: 1,
            });
          }

          noFaceFrameCount = 0;
          const gazeResult = analyzeGaze(detection);
          const isGazingAway = gazeResult.away;
          const gazeDirection = gazeResult.direction;
          if (!isGazingAway) {
            setEyeContact('Good');
            lastEyeContactTime.current = Date.now();
            awayStartTimeRef.current = null;
          } else {
            setEyeContact('Lost');
            if (!awayStartTimeRef.current) {
              awayStartTimeRef.current = Date.now();
            }
            const awayDuration = Date.now() - awayStartTimeRef.current;
            if (awayDuration >= EYE_GAZE_AWAY_THRESHOLD_MS) {
              reportViolation('eye_gaze_away', 'Candidate appears to be looking away', {
                reason: 'gaze-analysis',
                direction: gazeDirection,
                awayDurationMs: awayDuration,
              });
              awayStartTimeRef.current = null;
              if (screenshotManagerRef.current) {
                const now = Date.now();
                const lastEyeShot = lastViolationScreenshotRef.current['eye_gaze_away'] || 0;
                const underLimit = sessionScreenshotCountRef.current < MAX_SESSION_SCREENSHOTS;

                if (underLimit && (now - lastEyeShot) > 30000) {
                  lastViolationScreenshotRef.current['eye_gaze_away'] = now;
                  sessionScreenshotCountRef.current += 1;
                  screenshotManagerRef.current.capture('violation', {
                    violationType: 'eye_gaze_away',
                  });
                }
              }
            }
          }
          
          // Check for multiple faces.
          // Filter ghost detections: reflections in glasses, AI avatar on screen, clothing patterns.
          // Also require 8 consecutive positive frames (~0.5s) before logging — one bad frame is not a violation.
          const validFaceDetections = results.detections.filter(d => {
            const score = Array.isArray(d.score) ? (d.score[0] ?? 0) : (d.score ?? 0);
            const w = d.boundingBox?.width ?? 0;
            return score >= 0.65 && w >= 0.08;
          });
          if (validFaceDetections.length > 1) {
            multipleFaceFrameCount += 1;
            if (multipleFaceFrameCount >= 8) {
              reportViolation('multiple_faces', 'Multiple faces detected', {
                faceCount: validFaceDetections.length,
              });
              if (screenshotManagerRef.current) {
                const now = Date.now();
                const underLimit = sessionScreenshotCountRef.current < MAX_SESSION_SCREENSHOTS;
                const lastFaceShot = lastViolationScreenshotRef.current['multiple_faces'] || 0;
                if (underLimit && (now - lastFaceShot) > 30000) {
                  lastViolationScreenshotRef.current['multiple_faces'] = now;
                  sessionScreenshotCountRef.current += 1;
                  screenshotManagerRef.current.capture('violation', {
                    violationType: 'multiple_faces',
                    faceCount: validFaceDetections.length,
                  });
                }
              }
            }
          } else {
            multipleFaceFrameCount = 0;
          }
        } else {
          multipleFaceFrameCount = 0;
          noFaceFrameCount += 1;
          setFaceDetected(false);
          setEyeContact('Lost');
          if (noFaceFrameCount === NO_FACE_THRESHOLD) {
            reportViolation('face_not_visible', 'Face not visible for sustained duration', {
              duration_frames: noFaceFrameCount,
            });
            if (screenshotManagerRef.current) {
              screenshotManagerRef.current.capture('violation', {
                violationType: 'face_not_visible',
                durationFrames: noFaceFrameCount,
              });
            }
          }
        }

        // Draw on canvas for internal proctoring / lip-sync analysis. Screenshots
        // (report photo, evidence, moderation samples) are taken from this canvas.
        // Paint the live camera <video>, not MediaPipe's results.image: that is a
        // GPU/WebGL-backed frame that often copies to a 2D canvas as solid black.
        const frameSource = videoRef.current && videoRef.current.readyState >= 2 ? videoRef.current : results.image;
        if (canvasRef.current && videoRef.current && frameSource) {
          const vw = videoRef.current.videoWidth || 640;
          const vh = videoRef.current.videoHeight || 480;
          if (canvasRef.current.width !== vw || canvasRef.current.height !== vh) {
            canvasRef.current.width = vw;
            canvasRef.current.height = vh;
          }
          const ctx = canvasRef.current.getContext('2d');
          ctx.save();
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.drawImage(
            frameSource,
            0,
            0,
            canvasRef.current.width,
            canvasRef.current.height
          );
          ctx.restore();
        }

          // Lip sync analysis — runs every frame, lightweight pixel diff (after canvas draw)
        const currentDetection = results.detections?.[0] ?? null;
        analyzeLipSyncRef.current?.(canvasRef.current, currentDetection);

        // Identity check every ~15 face-present frames to avoid CPU spikes
        if (
          identityCheckerRef.current
          && results.detections?.length === 1
          && videoRef.current
        ) {
          identityFrameSkipRef.current += 1;
          if (identityFrameSkipRef.current >= 15) {
            identityFrameSkipRef.current = 0;
            const idResult = identityCheckerRef.current.evaluate(videoRef.current);
            if (idResult.match === false) {
              const now = Date.now();
              if (now - lastIdentityReportRef.current > 10000) {
                lastIdentityReportRef.current = now;
                reportViolation(
                  'candidate_identity_mismatch',
                  IDENTITY_MISMATCH_MSG,
                  { similarity: idResult.similarity },
                  'critical'
                );
              }
            }
          }
        }
      } catch (error) {
        console.warn('⚠️ Error processing face detection results:', error);
      }
      });
    };

    const createFaceDetectionInstance = () => {
      const fd = new FaceDetection({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
      });
      fd.setOptions({
        model: 'short',
        minDetectionConfidence: 0.65,
      });
      attachOnResults(fd);
      return fd;
    };

    const handleFrameError = (err) => {
      const isFatal =
        err?.name === 'BindingError' ||
        String(err?.message).includes('deleted object') ||
        String(err?.message).includes('SolutionWasm') ||
        String(err?.message).includes('Module.arguments') ||
        String(err?.message).includes('Aborted') ||
        String(err?.message).includes('ROI width and height');

      if (isFatal && !fatalFallbackUsed) {
        fatalFallbackUsed = true;
        fatalErrorRef.current = false;
        try {
          faceDetection?.close?.();
        } catch (_) {}
        try {
          faceDetection = createFaceDetectionInstance();
          faceDetectionRef.current = faceDetection;
          console.warn('⚠️ Face detection crashed — created fresh instance');
          return false;
        } catch (recreateErr) {
          console.warn('⚠️ Face detection fallback failed:', recreateErr);
        }
      }

      if (isFatal) {
        fatalErrorRef.current = true;
        try {
          camera?.stop();
        } catch (_) {}
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        try {
          faceDetection?.close?.();
        } catch (_) {}
        console.warn('🛑 Face detection fatal error — stopped permanently');
        return true;
      }

      console.warn('⚠️ Face detection frame error:', err);
      return false;
    };

    const processFrame = async () => {
      if (fatalErrorRef.current) return;
      const videoElement = videoRef.current;
      if (
        isProcessing ||
        !faceDetection ||
        !videoElement ||
        videoElement.readyState < 2 ||
        videoElement.videoWidth === 0 ||
        videoElement.videoHeight === 0
      ) {
        return;
      }

      try {
        isProcessing = true;
        await faceDetection.send({ image: videoElement });
        if (!onReadyCalledRef.current) {
          onReadyCalledRef.current = true;
          setTimeout(() => onReady?.(), livePrewarmedStream ? 0 : 500);
        }
      } catch (err) {
        handleFrameError(err);
      } finally {
        isProcessing = false;
      }
      void detectFrameRef.current?.(videoElement, lastFaceCountRef.current);
    };

    const startFrameLoop = () => {
      const tick = () => {
        if (cancelled) return;
        void processFrame();
        animationFrameId = requestAnimationFrame(tick);
      };
      animationFrameId = requestAnimationFrame(tick);
    };

    try {
      faceDetection = createFaceDetectionInstance();
      faceDetectionRef.current = faceDetection;
    } catch (error) {
      console.warn('⚠️ Face detection initialization failed:', error);
      return;
    }

    if (!videoRef.current) return;

    let voiceCancelled = false;

    videoRef.current.onloadedmetadata = () => {
      setStream(videoRef.current?.srcObject || null);
    };

    void (async () => {
      try {
        if (showCameraOnly && canvasRef.current) {
          canvasRef.current.width = Math.floor(window.innerWidth / 2);
          canvasRef.current.height = Math.floor(window.innerHeight - 60);
        }

        if (livePrewarmedStream) {
          videoRef.current.srcObject = pwStream;
          await waitForVideoReady(videoRef.current);
          if (cancelled) return;
          await videoRef.current.play().catch(() => {});
          setStream(pwStream);
          onVideoReady?.(videoRef.current);
          startFrameLoop();
        } else {
          await waitForVideoReady(videoRef.current);
          if (cancelled) return;
          camera = new Camera(videoRef.current, {
            onFrame: async () => {
              await processFrame();
            },
            width: showCameraOnly ? Math.floor(window.innerWidth / 2) : 640,
            height: showCameraOnly ? Math.floor(window.innerHeight - 60) : 480,
          });
          camera.start();
          setStream(videoRef.current?.srcObject || null);
        }

        if (voiceCancelled) return;
        startMonitorCheck();
        const lipSyncStream = videoRecorderRef?.current?.combinedStream
          ? new MediaStream(videoRecorderRef.current.combinedStream.getAudioTracks())
          : null;
        void startLipSync(lipSyncStream);
      } catch (error) {
        console.warn('⚠️ Camera initialization failed:', error);
      }
    })();

    return () => {
      cancelled = true;
      fatalErrorRef.current = false;
      voiceCancelled = true;
      try {
        isProcessing = false;
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        if (camera) {
          camera.stop();
        }
        if (faceDetection) {
          try {
            faceDetection.close();
          } catch (_) {}
        }
        if (usingPrewarmedStreamRef.current && pwStream?.active && !preserveStreamOnUnmountRef.current) {
          pwStream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch (_) {}
          });
        }
        faceDetectionRef.current = null;
        setStream(null);
        if (randomScreenshotIntervalRef.current) {
          clearInterval(randomScreenshotIntervalRef.current);
        }
        if (screenshotManagerRef.current) {
          screenshotManagerRef.current.uploadPending();
        }
        cleanupObjectDetection();
        stopVoice();
        stopMonitorCheck();
        stopLipSync();
      } catch (error) {
        console.warn('⚠️ Error cleaning up proctoring:', error);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialize once; stream captured in ref
  }, []);

  // ✅ FEATURE 2: Initialize screenshot manager and random screenshots
  useEffect(() => {
    if (activeId && canvasRef.current) {
      screenshotManagerRef.current = new ScreenshotManager(canvasRef, activeId);
      
      // Start random screenshots every 3 minutes
      randomScreenshotIntervalRef.current = setInterval(() => {
        if (screenshotManagerRef.current && faceDetected) {
          if (sessionScreenshotCountRef.current < MAX_SESSION_SCREENSHOTS) {
            sessionScreenshotCountRef.current += 1;
            screenshotManagerRef.current.capture('random', {
              autoCaptured: true
            });
          }
        }
      }, 180000);

      // Step 10 — content moderation sampling, independent of the
      // violation-evidence budget above. The backend runs every uploaded
      // screenshot through moderation regardless of type, so this just
      // needs to keep frames arriving through the interview.
      moderationSampleIntervalRef.current = setInterval(() => {
        if (screenshotManagerRef.current && moderationSampleCountRef.current < MAX_MODERATION_SAMPLES) {
          moderationSampleCountRef.current += 1;
          screenshotManagerRef.current.capture('moderation_sample', {
            autoCaptured: true
          });
        }
      }, MODERATION_SAMPLE_INTERVAL_MS);

      return () => {
        if (randomScreenshotIntervalRef.current) {
          clearInterval(randomScreenshotIntervalRef.current);
        }
        if (moderationSampleIntervalRef.current) {
          clearInterval(moderationSampleIntervalRef.current);
        }
      };
    }
  }, [activeId, faceDetected]);

  useEffect(() => {
    if (!manageBrowserEvents) return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('TAB SWITCH DETECTED');
        reportViolation('tab_switch', 'Tab switched');
        setTabSwitches((prev) => {
          const newCount = prev + 1;
          onViolation?.({ type: 'tab_switch', count: newCount });
          return newCount;
        });
      }
    };

    const handleBlur = () => {
      reportViolation('window_blur', 'Window lost focus');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportViolation, onViolation, manageBrowserEvents]);

  // Eye gaze is now handled in faceDetection.onResults using face position
  // Removed the old interval-based check

  if (showCameraOnly) {
    return (
      <div className="proctoring-monitor camera-only">
        <div className="video-container-fullscreen" style={{ position: 'relative', overflow: 'hidden' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              inset: 0,
              background: '#0f172a',
            }}
          />
          <canvas
            ref={canvasRef}
            className="proctoring-canvas-fullscreen"
            style={{ display: 'none' }}
          />
          {/* Face detection still runs for proctoring; do not cover the self-view with a status banner. */}
        </div>
      </div>
    );
  }

  return (
    <div className="proctoring-monitor">
      <h3>Proctoring Monitor</h3>
      <div className="monitor-status">
        <div className="status-item">
          <span className={`status-indicator ${faceDetected ? 'active' : 'inactive'}`}></span>
          <span>Face Detection: {faceDetected ? 'Active' : 'Inactive'}</span>
        </div>
        <div className="status-item">
          <span className={`status-indicator ${eyeContact === 'Good' ? 'active' : 'inactive'}`}></span>
          <span>Eye Contact: {eyeContact}</span>
        </div>
        <div className="status-item">
          <span>Tab Switches: {tabSwitches}</span>
        </div>
      </div>
      <div className="video-container">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ display: 'none' }}
        />
        <canvas
          ref={canvasRef}
          width={640}
          height={480}
          className="proctoring-canvas"
        />
      </div>
    </div>
  );
};

